import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Divider,
  Typography,
} from '@mui/material';
import { lazy, Suspense, useState } from 'react';
import SettingsMenu, { MenuItemType } from '@/components/SettingsMenu';
import { AddCircleOutline, DeleteOutlined, Download, InfoOutlined } from '@mui/icons-material';
import LtDialog from '@/components/LtDialog';
import { useTranslation } from 'react-i18next';
import useAssets from '@/infrastructure/hooks/useAssets';
import { ASSET_PATHS } from '@lemontaps/lib/constants';
import useLtNotifications from '@/infrastructure/notifications/useLtNotifications';

const AddSignatureDialog = lazy(() => import('./AddSignatureDialog'));

type Props = {
  value: { id: string; location: string } | null;
  onChange: (value: { id: string; location: string } | null) => void;
  onDownload: () => void;
  labelElement?: React.ReactNode;
  title?: string;
  description?: string;
  createdOn?: string;
  canDownload?: boolean;
};

export const Signature = ({
  labelElement,
  value,
  onChange,
  onDownload,
  title,
  description,
  createdOn,
  canDownload = true,
}: Props) => {
  const { notify } = useLtNotifications();
  const { t } = useTranslation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addSignatureDialogOpen, setAddSignatureDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { allocateAsset } = useAssets();

  const handleSave = async (file: File) => {
    setUploading(true);
    try {
      const asset = await allocateAsset(file, ASSET_PATHS.LeadSignature);
      onChange({ id: asset.id, location: asset.location });
      setAddSignatureDialogOpen(false);
    } catch (error) {
      notify.error(t('error.general'));
    }
    setUploading(false);
  };

  const menuItems = [
    {
      label: t('signature.delete'),
      icon: <DeleteOutlined />,
      onClick: () => setDeleteDialogOpen(true),
    },
    canDownload && {
      label: t('signature.download'),
      icon: <Download />,
      onClick: onDownload,
    },
  ].filter(Boolean) as MenuItemType[];

  return (
    <Box mt={1}>
      <Card>
        <CardHeader
          title={labelElement}
          action={
            <Box sx={value ? {} : { opacity: 0, pointerEvents: 'none' }}>
              <SettingsMenu menuItems={menuItems} />
            </Box>
          }
          sx={{ py: 0.5 }}
        />
        {value ? (
          <>
            <Divider />
            <CardMedia component='img' src={value?.location || ''} alt='signature' />
          </>
        ) : (
          <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              endIcon={<AddCircleOutline />}
              variant='contained'
              color='primary'
              onClick={() => setAddSignatureDialogOpen(true)}
            >
              {t('signature.add')}
            </Button>
          </CardContent>
        )}
        {createdOn && (
          <>
            <Divider />
            <CardActions>
              <Typography variant='body2' color='text.secondary'>
                {createdOn}
              </Typography>
            </CardActions>
          </>
        )}
      </Card>
      <LtDialog
        title={t('requestDelete')}
        titleIcon={<InfoOutlined color='warning' />}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onCancel={() => setDeleteDialogOpen(false)}
        onDelete={() => {
          onChange(null);
          setDeleteDialogOpen(false);
        }}
      >
        {t('genericDelete')}
      </LtDialog>
      <Suspense fallback={null}>
        <AddSignatureDialog
          open={addSignatureDialogOpen}
          onClose={() => setAddSignatureDialogOpen(false)}
          onSave={handleSave}
          uploading={uploading}
          title={title}
          description={description}
        />
      </Suspense>
    </Box>
  );
};
